<script setup lang="ts">
import { useDrawingStore } from "@/stores/fileStore";
import { useDeviceState } from "@/stores/DeviceState";
import { Logger } from "@/logger";
import { ref } from "vue";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import UserState from "@/stores/UserState";
import { useNavigate } from "@/composable/useNavigate";
import DRResponsiveBtn from "../UI/DRResponsiveBtn.vue";
import DREditTextDialog from "../UI/feedbacks/DREditTextDialog.vue";
import { useTranslation } from "i18next-vue";

const props = withDefaults(
  defineProps<{
    mini?: boolean;
    projectId: number;
  }>(),
  {
    mini: false,
  }
);
const { navigateCanva: navigate } = useNavigate();
const { t } = useTranslation();

const fileInput = ref<HTMLInputElement | null>(null);
const showNameDialog = ref(false);

const deviceState = useDeviceState();

const uuid = Math.random().toString();
const drawingStore = useDrawingStore();

async function uploadFile(event: Event) {
  const files = (event.target as HTMLInputElement).files;
  if (files) {
    Logger.info(`AddFile.vue : uploading files, ${files.length}`);
    try {
      drawingStore.uploadFiles(Array.from(files), props.projectId);
    } catch(e) {
      Logger.error(`AddFile.vue : could not upload file, ${JSON.stringify(e)}`);
      throw e;
    }
  } else {
    Logger.info(`AddFile.vue : No file upaloaded`);
  }
}

function openFileDialog() {
  fileInput.value?.click();
}

function openCreateDrawingDialog() {
  showNameDialog.value = true;
}

async function createDrawing(name: string) {
  const user = UserState.data;
  if (!user?.organization) {
    Logger.error("AddFileButton.vue : User ID is not available");
    return;
  }

  // If no name is provided, use the default translation
  const drawingName = name.trim() || t("dashboard:drawings.new.plan");

  const { id } = await localDrawingRepository.createTemporaryDrawing({
    projectId: props.projectId,
    name: drawingName,
    organizationId: user.organization.id,
    user: {
      id: user.id,
      full_name: user.full_name,
    },
  });

  navigate(id);
}
</script>

<template>
  <DRResponsiveBtn
    icon="bi-plus-lg"
    variant="outline"
    :label="$t('dashboard:drawings.new.plan')"
    @click="openCreateDrawingDialog"
  />

  <DREditTextDialog
    v-model="showNameDialog"
    :title="$t('dashboard:drawings.new.plan')"
    :placeholder="$t('dashboard:drawings.new.plan')"
    @confirm="createDrawing"
  />

  <input
    ref="fileInput"
    type="file"
    class="d-none file-input-dwg"
    :id="uuid"
    multiple
    accept=".dwg"
    @change.prevent="uploadFile"
  />

  <DRResponsiveBtn
    :class="$attrs.class"
    icon="bi-upload"
    :label="$t('import.plan.dwg')"
    :disabled="!deviceState.isOnline"
    @click="openFileDialog"
  />
</template>
